import { styled } from "styled-components";

import { UIButton, UIChip } from "common/components";
import { StyledSectionTitle } from "common/components/SectionTitleUnderlined";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { ModalHeader } from "domains/modal/components/ModalHeader";
import { getModalParams } from "domains/modal/selectors";
import type { ModalParams } from "domains/modal/types";
import type { FilterField } from "domains/topicSearchFilter/types";

const StyledContainer = styled.div`
  margin-bottom: 2rem;
`;

const StyledSectionTitleContainer = styled.div`
  margin-bottom: 1rem;
`;

const StyledTag = styled(UIChip)`
  margin: 0 10px 10px 0;
`;

type TopicFilterModalProps = {
  dataTestId?: string;
  fields?: FilterField[];
  handleApplyFilter?: (filter: { topicId: string | undefined } & Partial<Record<string, string>>) => () => void;
  handleClear?: (topicId?: string) => void;
  searchCriteria?: Record<string, string | number>;
  title?: string;
};

export const TopicFilterModal = ({
  title,
  fields = [],
  searchCriteria = {},
  handleApplyFilter,
  handleClear,
  dataTestId = "TopicFilterModal",
}: TopicFilterModalProps) => {
  const { selectedTopicId } = useSelector(getModalParams) as ModalParams<
    | "TopicDataFilter"
    | "TopicEventFilter"
    | "TopicInitiativeFilter"
    | "TopicLatestFilter"
    | "TopicPublicationFilter"
    | "TopicStakeholderFilter"
    | "TopicVideoFilter"
  >;
  const translate = useTranslate();

  return (
    <div data-test-id={dataTestId}>
      <ModalHeader title={title ?? translate("app.filter.title")} />
      {fields.map((field) => (
        <StyledContainer key={field.id} data-test-id={`${dataTestId}-${field.id}`}>
          <StyledSectionTitleContainer>
            <StyledSectionTitle>{field.label}</StyledSectionTitle>
          </StyledSectionTitleContainer>
          {field.options.map((item) => (
            <StyledTag
              key={item.id}
              isActive={searchCriteria[field.id] === item.id}
              onClick={handleApplyFilter?.({ topicId: selectedTopicId, [field.id]: item.id })}
              dataTestId={`${dataTestId}-${field.id}-${item.id}`}
            >
              {item.label}
            </StyledTag>
          ))}
        </StyledContainer>
      ))}
      <UIButton data-test-id={`${dataTestId}-clear`} fullwidth onClick={() => handleClear?.()} variant="filled">
        {translate("app.search_and_filter.clear")}
      </UIButton>
    </div>
  );
};
